if (!customElements.get('sticky-product-atc')) {
  customElements.define(
    'sticky-product-atc',
    class StickyProductAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';

        this.mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.buyButtons =
          document.getElementById(`product-buy-buttons-${this.sectionId}`) ||
          this.mainSubmit?.closest('.product-form__buttons');
        this.stickySubmit = this.querySelector('.sticky-atc__submit');
        this.stickyPrice = this.querySelector('.sticky-atc__price');
        this.stickyImages = this.querySelectorAll('.sticky-atc__image');
        this.priceSource = document.getElementById(`price-${this.sectionId}`);
        this.variantDropdown = this.querySelector('[data-variant-dropdown]');
        this.variantTrigger = this.querySelector('.sticky-atc__variant-trigger');
        this.variantMenu = this.querySelector('.sticky-atc__variant-menu');
        this.variantLabel = this.querySelector('[data-variant-label]');
        this.variantOptions = this.querySelectorAll('.sticky-atc__variant-option');
        this.variantSelect = this.querySelector('.sticky-atc__variant-select');
        this.stickyQtyInput = this.querySelector('.sticky-atc__qty-input');
        this.mainQtyInput = document.getElementById(`Quantity-${this.sectionId}`);
        this.mainQtyWrap = this.mainQtyInput?.closest('quantity-input');

        this.variants = this.loadVariants();

        if (!this.mainSubmit || !this.buyButtons) {
          this.remove();
          return;
        }

        this.bindEvents();
        this.syncQuantityFromMain();
        this.syncButtonState();
        this.syncLoadingState();

        this.observer = new IntersectionObserver(
          ([entry]) => this.toggleVisible(!entry.isIntersecting),
          { threshold: 0, rootMargin: '0px' }
        );
        this.observer.observe(this.buyButtons);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (String(event.data?.sectionId) !== String(this.sectionId)) return;
            this.syncFromVariant(event.data);
          });
        }

        this.resizeObserver = new ResizeObserver(() => {
          if (this.classList.contains('sticky-atc--visible')) {
            this.updateFloatingOffset(true);
          }
        });
        this.resizeObserver.observe(this);
      }

      loadVariants() {
        const jsonEl = document.getElementById(`StickyVariantsJson-${this.sectionId}`);
        if (!jsonEl) return [];
        try {
          return JSON.parse(jsonEl.textContent);
        } catch {
          return [];
        }
      }

      bindEvents() {
        this.stickySubmit?.addEventListener('click', () => {
          if (
            !this.mainSubmit.disabled &&
            this.mainSubmit.getAttribute('aria-disabled') !== 'true' &&
            !this.mainSubmit.classList.contains('loading')
          ) {
            this.mainSubmit.click();
          }
        });

        this.loadingObserver = new MutationObserver(() => this.syncLoadingState());
        this.loadingObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['class', 'aria-disabled', 'disabled'],
        });

        this.querySelector('.sticky-atc__quickview')?.addEventListener('click', () => {
          const target =
            document.querySelector('.product__media-wrapper') ||
            document.querySelector(`#ProductInfo-${this.sectionId}`) ||
            document.getElementById('MainContent');
          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });

        this.initVariantDropdown();

        this.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
          const minus = this.mainQtyWrap?.querySelector('button[name="minus"]');
          if (minus) minus.click();
          else this.changeQuantity(-1);
        });

        this.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
          const plus = this.mainQtyWrap?.querySelector('button[name="plus"]');
          if (plus) plus.click();
          else this.changeQuantity(1);
        });

        this.stickyQtyInput?.addEventListener('change', () => this.syncQuantityToMain());

        this.mainQtyInput?.addEventListener('change', () => this.syncQuantityFromMain());
        this.mainQtyInput?.addEventListener('input', () => this.syncQuantityFromMain());
      }

      initVariantDropdown() {
        if (!this.variantTrigger || !this.variantMenu) return;

        this.variantTrigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleVariantMenu();
        });

        this.variantOptions.forEach((option) => {
          option.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (option.disabled) return;

            const variantId = parseInt(option.dataset.variantId, 10);
            const variant = this.variants.find((item) => item.id === variantId);
            if (variant) {
              this.setVariantDropdownValue(variantId, option.dataset.variantLabel);
              this.applyVariant(variant);
            }
            this.closeVariantMenu();
          });
        });

        this.variantOutsideClick = (event) => {
          if (!this.variantDropdown?.contains(event.target)) {
            this.closeVariantMenu();
          }
        };

        this.variantEscapeKey = (event) => {
          if (event.key === 'Escape') this.closeVariantMenu();
        };

        document.addEventListener('click', this.variantOutsideClick);
        document.addEventListener('keydown', this.variantEscapeKey);
      }

      toggleVariantMenu() {
        if (this.variantMenu.hidden) {
          this.openVariantMenu();
        } else {
          this.closeVariantMenu();
        }
      }

      openVariantMenu() {
        if (!this.variantMenu || !this.variantTrigger) return;
        this.variantMenu.hidden = false;
        this.variantTrigger.setAttribute('aria-expanded', 'true');
        this.variantDropdown?.classList.add('is-open');
      }

      closeVariantMenu() {
        if (!this.variantMenu || !this.variantTrigger) return;
        this.variantMenu.hidden = true;
        this.variantTrigger.setAttribute('aria-expanded', 'false');
        this.variantDropdown?.classList.remove('is-open');
      }

      setVariantDropdownValue(variantId, label) {
        if (this.variantSelect) {
          this.variantSelect.value = String(variantId);
        }

        const selectedOption = Array.from(this.variantOptions || []).find(
          (option) => parseInt(option.dataset.variantId, 10) === variantId
        );
        const optionText = selectedOption?.querySelector('.sticky-atc__variant-option-text');

        if (optionText && this.variantLabel) {
          this.variantLabel.innerHTML = optionText.innerHTML;
        } else if (label && this.variantLabel) {
          this.variantLabel.textContent = label;
        }

        this.variantOptions.forEach((option) => {
          const isSelected = parseInt(option.dataset.variantId, 10) === variantId;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
      }

      applyVariant(variant) {
        const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
        let updated = false;

        if (variantSelects) {
          variant.options.forEach((value) => {
            if (!value) return;

            const radio = Array.from(variantSelects.querySelectorAll('input[type="radio"]')).find(
              (input) => input.value === value
            );
            if (radio && !radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
              updated = true;
            }

            variantSelects.querySelectorAll('select').forEach((select) => {
              const option = Array.from(select.options).find((opt) => opt.value === value);
              if (option && select.value !== value) {
                select.value = value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
                updated = true;
              }
            });
          });
        }

        if (!updated) {
          const input = document.querySelector(`#${this.formId} input[name="id"]`);
          if (input) {
            input.value = variant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        const selectedOption = Array.from(this.variantOptions || []).find(
          (option) => parseInt(option.dataset.variantId, 10) === variant.id
        );
        this.setVariantDropdownValue(variant.id, selectedOption?.dataset.variantLabel);
      }

      changeQuantity(delta) {
        if (!this.stickyQtyInput) return;
        const step = parseInt(this.stickyQtyInput.step, 10) || 1;
        const min = parseInt(this.stickyQtyInput.min, 10) || 1;
        const max = this.stickyQtyInput.max ? parseInt(this.stickyQtyInput.max, 10) : null;
        let next = parseInt(this.stickyQtyInput.value, 10) + delta * step;
        if (Number.isNaN(next)) next = min;
        if (next < min) next = min;
        if (max && next > max) next = max;
        this.stickyQtyInput.value = next;
        this.syncQuantityToMain();
      }

      syncQuantityFromMain() {
        if (!this.stickyQtyInput || !this.mainQtyInput) return;
        this.stickyQtyInput.value = this.mainQtyInput.value;
        if (this.mainQtyInput.min) this.stickyQtyInput.min = this.mainQtyInput.min;
        if (this.mainQtyInput.max) this.stickyQtyInput.max = this.mainQtyInput.max;
        if (this.mainQtyInput.step) this.stickyQtyInput.step = this.mainQtyInput.step;
      }

      syncQuantityToMain() {
        if (!this.stickyQtyInput || !this.mainQtyInput) return;
        this.mainQtyInput.value = this.stickyQtyInput.value;
        this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      toggleVisible(show) {
        if (show) {
          this.hidden = false;
          requestAnimationFrame(() => {
            this.classList.add('sticky-atc--visible');
            this.updateFloatingOffset(true);
          });
        } else {
          this.classList.remove('sticky-atc--visible');
          this.updateFloatingOffset(false);
          window.setTimeout(() => {
            if (!this.classList.contains('sticky-atc--visible')) {
              this.hidden = true;
            }
          }, 520);
        }

        document.body.classList.toggle('has-sticky-atc', show);
      }

      updateFloatingOffset(show) {
        if (show) {
          document.body.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
        } else {
          document.body.style.removeProperty('--sticky-atc-height');
        }
      }

      syncFromVariant({ variant }) {
        if (this.priceSource && this.stickyPrice) {
          this.stickyPrice.innerHTML = this.priceSource.innerHTML;
        }

        if (variant?.featured_image?.src && this.stickyImages?.length) {
          this.stickyImages.forEach((image) => {
            image.src = variant.featured_image.src;
            if (variant.featured_image.alt) {
              image.alt = variant.featured_image.alt;
            }
          });
        }

        if (variant?.id) {
          const selectedOption = Array.from(this.variantOptions || []).find(
            (option) => parseInt(option.dataset.variantId, 10) === variant.id
          );
          this.setVariantDropdownValue(variant.id, selectedOption?.dataset.variantLabel);
        }

        this.syncQuantityFromMain();
        this.syncButtonState();
      }

      syncButtonState() {
        if (!this.mainSubmit || !this.stickySubmit) return;

        const isLoading = this.mainSubmit.classList.contains('loading');
        const disabled =
          this.mainSubmit.disabled ||
          this.mainSubmit.getAttribute('aria-disabled') === 'true' ||
          isLoading;
        this.stickySubmit.disabled = disabled;

        const labelEl = this.stickySubmit.querySelector('.sticky-atc__submit-text');
        const labelSpan = this.mainSubmit.querySelector('span:not(.sold-out-message):not(.hidden)');
        const label = labelSpan?.textContent?.trim();
        if (labelEl && label) {
          labelEl.textContent =
            label.toLowerCase().includes('sold') || label.toLowerCase().includes('unavailable')
              ? label.toUpperCase()
              : 'ADD TO CART';
        }
      }

      syncLoadingState() {
        if (!this.mainSubmit || !this.stickySubmit) return;

        const isLoading = this.mainSubmit.classList.contains('loading');
        this.stickySubmit.classList.toggle('loading', isLoading);

        const spinner = this.stickySubmit.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);

        this.syncButtonState();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.loadingObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.variantUnsub?.();
        if (this.variantOutsideClick) {
          document.removeEventListener('click', this.variantOutsideClick);
        }
        if (this.variantEscapeKey) {
          document.removeEventListener('keydown', this.variantEscapeKey);
        }
        document.body.classList.remove('has-sticky-atc');
        document.body.style.removeProperty('--sticky-atc-height');
      }
    }
  );
}
